import { useState } from 'react';
import { useTheme } from '../hooks/useTheme';
import { COLORS, GRADIENTS, FONTS, STATUS_COLORS } from '../styles/tokens';
import type { AppDefinition } from '../data/apps';
import { AppIcon } from './AppIcon';

/** Plate size on the app card. Large enough to read as the card's subject. */
const ICON_SIZE = 96;

interface AppCardProps {
  app: AppDefinition;
  isFavorite?: boolean;
  onToggleFavorite?: (appId: string) => void;
  /** Locked = the user lacks access; the card cannot be launched. */
  locked?: boolean;
}

/** Redesigned app card: name top-left, large icon, status/team rows, launch button */
export function AppCard({ app, isFavorite, onToggleFavorite, locked }: AppCardProps): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;
  const [isHovered, setIsHovered] = useState(false);

  const isMaintenance = app.status === 'maintenance';
  const statusColor = STATUS_COLORS[app.status] ?? STATUS_COLORS.loading;

  function handleLaunch(): void {
    if (locked) return;
    if (app.launchUrl !== '#') {
      window.open(app.launchUrl, '_blank', 'noopener,noreferrer');
    }
  }

  const cardShadow = isDark
    ? isHovered
      ? '0 8px 32px rgba(0, 0, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.3)'
      : '0 4px 16px rgba(0, 0, 0, 0.3), 0 1px 4px rgba(0, 0, 0, 0.2)'
    : isHovered
      ? '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)'
      : '0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)';

  return (
    <div
      className="vx-card-hover"
      style={{
        backgroundColor: colors.card,
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        boxShadow: cardShadow,
        borderRadius: '16px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        cursor: 'default',
        transition: 'box-shadow 0.25s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Name + star + live dot */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3
          className="vx-app-card-title vx-track"
          style={{
            fontFamily: FONTS.heading,
            fontSize: '14px',
            fontWeight: 400,
            letterSpacing: '0.15em',
            color: colors.text,
            textTransform: 'uppercase',
            margin: 0,
            minWidth: 0,
          }}
        >
          {app.name}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onToggleFavorite && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onToggleFavorite(app.id); }}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                display: 'grid',
                placeItems: 'center',
                width: '20px',
                height: '20px',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill={isFavorite ? '#F4C43F' : 'none'} stroke={isFavorite ? '#F4C43F' : colors.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </button>
          )}
          <div
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: statusColor,
              boxShadow: `0 0 6px ${statusColor}40`,
              flexShrink: 0,
            }}
          />
        </div>
      </div>

      {/* App icon. The icon's own plate is the only frame. The previous
          wrapper set `aspectRatio: 1` alongside `maxHeight: 120px`, which a
          full-width card resolved to a wide letterbox holding a 56px mark. */}
      <div style={{ display: 'grid', placeItems: 'center', padding: '8px 0' }}>
        <AppIcon name={app.name} icon={app.icon} iconDark={app.iconDark} isDark={isDark} size={ICON_SIZE} />
      </div>

      {/* Status row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span
            className="vx-track"
            style={{
              fontFamily: FONTS.body,
              fontSize: '10px',
              color: colors.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}
          >
            STATUS
          </span>
          <p
            className="vx-track"
            style={{
              fontFamily: FONTS.body,
              fontSize: '12px',
              color: statusColor,
              margin: 0,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {app.status}
          </p>
        </div>
        {app.team && (
          <div style={{ textAlign: 'right' }}>
            <span
              style={{
                fontFamily: FONTS.body,
                fontSize: '10px',
                color: colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
              }}
            >
              TEAM
            </span>
            <p
              style={{
                fontFamily: FONTS.body,
                fontSize: '12px',
                color: colors.text,
                margin: '2px 0 0',
              }}
            >
              {app.team}
            </p>
          </div>
        )}
      </div>

      {/* Launch button — hidden for maintenance apps. Locked (no access) apps
          show a non-actionable "Request Access" state instead of launching. */}
      {!isMaintenance && (
        locked ? (
          <button
            disabled
            title="You do not have access to this app"
            style={{
              width: '100%',
              height: '40px',
              background: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.textSecondary,
              fontFamily: FONTS.heading,
              fontSize: '12px',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              cursor: 'not-allowed',
              marginTop: '4px',
            }}
          >
            Request Access
          </button>
        ) : (
          <button
            onClick={handleLaunch}
            disabled={app.status === 'offline'}
            style={{
              width: '100%',
              height: '40px',
              background: isDark ? GRADIENTS.buttonDark : GRADIENTS.buttonLight,
              border: 'none',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontFamily: FONTS.heading,
              fontSize: '12px',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              cursor: app.status === 'offline' ? 'not-allowed' : 'pointer',
              opacity: app.status === 'offline' ? 0.5 : 1,
              transition: 'filter 0.2s',
              marginTop: '4px',
            }}
            onMouseEnter={e => {
              if (app.status !== 'offline') {
                e.currentTarget.style.filter = 'brightness(1.15)';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.filter = 'brightness(1)';
            }}
          >
            LAUNCH
          </button>
        )
      )}
    </div>
  );
}
